<template>
  <div class="banner-management">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <div>
            <span>轮播图管理</span>
            <p class="header-desc">支持图片与视频；一处上传，小程序首页与官网门户同步展示（可通过「适用端」单独配置）</p>
          </div>
          <el-button class="btn-primary" icon="el-icon-plus" @click="openCreate">新增轮播</el-button>
        </div>
      </template>

      <el-table
        :data="banners"
        class="banner-table"
        style="width: 100%"
        v-loading="loading"
        :show-header="true"
      >
        <el-table-column label="预览" width="150" class-name="nowrap-cell">
          <template slot-scope="scope">
            <div class="thumb-wrap">
              <el-image
                v-if="scope.row.media_type !== 'video'"
                :src="resolveImg(scope.row.image_url)"
                class="thumb-img"
                fit="cover"
              >
                <div slot="error" class="thumb-error">加载失败</div>
              </el-image>
              <div v-else class="thumb-video">
                <el-image
                  v-if="scope.row.image_url"
                  :src="resolveImg(scope.row.image_url)"
                  class="thumb-img"
                  fit="cover"
                >
                  <div slot="error" class="thumb-error">视频</div>
                </el-image>
                <div v-else class="thumb-error">视频</div>
                <span class="thumb-badge">视频</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="72" class-name="nowrap-cell">
          <template slot-scope="scope">{{ scope.row.media_type === 'video' ? '视频' : '图片' }}</template>
        </el-table-column>
        <el-table-column prop="link_url" label="跳转链接" min-width="160" show-overflow-tooltip class-name="nowrap-cell" />
        <el-table-column label="适用端" width="88" class-name="nowrap-cell">
          <template slot-scope="scope">{{ platformLabel(scope.row.platform) }}</template>
        </el-table-column>
        <el-table-column prop="sort_weight" label="排序" width="72" class-name="nowrap-cell" />
        <el-table-column label="状态" width="72" class-name="nowrap-cell">
          <template slot-scope="scope">
            <el-switch
              :value="scope.row.status === 'enabled'"
              @change="(v) => toggleStatus(scope.row, v)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="248" fixed="right" class-name="nowrap-cell">
          <template slot-scope="scope">
            <el-button size="mini" class="btn-secondary" @click="moveBanner(scope.row, 'up')">上移</el-button>
            <el-button size="mini" class="btn-secondary" @click="moveBanner(scope.row, 'down')">下移</el-button>
            <el-button size="mini" class="btn-secondary" @click="openEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" plain @click="removeBanner(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="dialogMode === 'create' ? '新增轮播' : '编辑轮播'"
      :visible.sync="dialogVisible"
      width="560px"
      destroy-on-close
      append-to-body
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="媒体类型" required>
          <el-radio-group v-model="form.media_type">
            <el-radio label="image">图片</el-radio>
            <el-radio label="video">视频</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.media_type === 'image'" label="轮播图片" required>
          <el-upload
            class="banner-uploader"
            :show-file-list="false"
            :http-request="handleImageUpload"
            accept="image/jpeg,image/png,image/webp"
          >
            <img v-if="form.image_url" :src="resolveImg(form.image_url)" class="banner-preview" />
            <i v-else class="el-icon-plus banner-uploader-icon"></i>
          </el-upload>
          <div class="form-tip">支持 JPG/PNG/WebP；小程序与官网按 16:9 裁切显示</div>
        </el-form-item>

        <template v-else>
          <el-form-item label="轮播视频" required>
            <el-upload
              class="banner-uploader"
              :show-file-list="false"
              :http-request="handleVideoUpload"
              accept="video/mp4,video/quicktime,.mp4,.mov,.m4v"
            >
              <div v-if="form.video_url" class="banner-video-preview">
                <video :src="resolveImg(form.video_url)" class="banner-preview-video" muted controls />
              </div>
              <i v-else class="el-icon-plus banner-uploader-icon"></i>
            </el-upload>
            <div class="form-tip">建议 MP4（H.264）、时长 10–20 秒、文件 5–10MB（最大 20MB）；小程序需配置 downloadFile 合法域名</div>
          </el-form-item>
          <el-form-item label="封面图">
            <el-upload
              class="banner-uploader"
              :show-file-list="false"
              :http-request="handlePosterUpload"
              accept="image/jpeg,image/png,image/webp"
            >
              <img v-if="form.image_url" :src="resolveImg(form.image_url)" class="banner-preview" />
              <i v-else class="el-icon-plus banner-uploader-icon"></i>
            </el-upload>
            <div class="form-tip">可选；未上传时小程序用视频首帧作封面</div>
          </el-form-item>
        </template>

        <el-form-item label="跳转链接">
          <el-input v-model="form.link_url" placeholder="小程序页路径或 https:// 链接，可留空" />
        </el-form-item>
        <el-form-item label="适用端">
          <el-radio-group v-model="form.platform">
            <el-radio label="all">全部</el-radio>
            <el-radio label="miniprogram">仅小程序</el-radio>
            <el-radio label="web">仅网页端</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序权重">
          <el-input-number v-model="form.sort_weight" :min="0" :max="99999" controls-position="right" />
          <div class="form-tip">数值越大越靠前</div>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="formEnabled" active-text="启用" inactive-text="禁用" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button class="btn-primary" :loading="saving" @click="submit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { apiBase } from '../api/client'
import { formatApiError } from '../utils/apiError'

export default {
  name: 'BannerManagement',
  data() {
    return {
      banners: [],
      loading: false,
      saving: false,
      dialogVisible: false,
      dialogMode: 'create',
      editingId: null,
      form: {
        media_type: 'image',
        image_url: '',
        video_url: '',
        link_url: '',
        platform: 'all',
        sort_weight: 100,
        status: 'enabled'
      }
    }
  },
  computed: {
    formEnabled: {
      get() {
        return this.form.status === 'enabled'
      },
      set(v) {
        this.form.status = v ? 'enabled' : 'disabled'
      }
    },
    assetOrigin() {
      return String(apiBase || '').replace(/\/api\/?$/, '') || (typeof window !== 'undefined' ? window.location.origin : '')
    }
  },
  mounted() {
    this.loadBanners()
  },
  methods: {
    resolveImg(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      if (s.startsWith('/uploads/')) return this.assetOrigin + s
      if (s.startsWith('/')) return this.assetOrigin + s
      return s
    },
    platformLabel(p) {
      const map = { all: '全部', miniprogram: '小程序', web: '网页端' }
      return map[p] || p || '全部'
    },
    emptyForm() {
      return {
        media_type: 'image',
        image_url: '',
        video_url: '',
        link_url: '',
        platform: 'all',
        sort_weight: 100,
        status: 'enabled'
      }
    },
    async loadBanners() {
      this.loading = true
      try {
        const resp = await this.$api.get('/admin/banners')
        if (resp && resp.code === 200) {
          this.banners = Array.isArray(resp.data) ? resp.data : []
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载轮播图失败'))
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.dialogMode = 'create'
      this.editingId = null
      this.form = this.emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogMode = 'edit'
      this.editingId = row.id
      this.form = {
        media_type: row.media_type === 'video' ? 'video' : 'image',
        image_url: row.image_url || '',
        video_url: row.video_url || '',
        link_url: row.link_url || '',
        platform: row.platform || 'all',
        sort_weight: Number(row.sort_weight) || 0,
        status: row.status || 'enabled'
      }
      this.dialogVisible = true
    },
    async uploadFile(param, kind) {
      if (!param.file) return null
      const formData = new FormData()
      formData.append('file', param.file)
      formData.append('kind', kind)
      const resp = await this.$api.post('/admin/banners/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      if (resp && resp.code === 200 && resp.data && resp.data.url) {
        return resp.data.url
      }
      throw new Error((resp && resp.message) || '上传失败')
    },
    async handleImageUpload(param) {
      try {
        const url = await this.uploadFile(param, 'banner')
        this.form.image_url = url
        this.$message.success('图片上传成功')
      } catch (e) {
        this.$message.error(formatApiError(e, '图片上传失败'))
      } finally {
        param.onSuccess && param.onSuccess()
      }
    },
    async handlePosterUpload(param) {
      try {
        const url = await this.uploadFile(param, 'banner')
        this.form.image_url = url
        this.$message.success('封面上传成功')
      } catch (e) {
        this.$message.error(formatApiError(e, '封面上传失败'))
      } finally {
        param.onSuccess && param.onSuccess()
      }
    },
    async handleVideoUpload(param) {
      try {
        const url = await this.uploadFile(param, 'video')
        this.form.video_url = url
        this.form.media_type = 'video'
        this.$message.success('视频上传成功')
      } catch (e) {
        this.$message.error(formatApiError(e, '视频上传失败'))
      } finally {
        param.onSuccess && param.onSuccess()
      }
    },
    async submit() {
      if (this.form.media_type === 'video') {
        if (!String(this.form.video_url || '').trim()) {
          this.$message.warning('请上传轮播视频')
          return
        }
      } else if (!String(this.form.image_url || '').trim()) {
        this.$message.warning('请上传轮播图')
        return
      }
      this.saving = true
      try {
        const payload = {
          media_type: this.form.media_type,
          image_url: this.form.image_url,
          video_url: this.form.media_type === 'video' ? this.form.video_url : '',
          link_url: this.form.link_url,
          platform: this.form.platform,
          sort_weight: this.form.sort_weight,
          status: this.form.status
        }
        if (this.dialogMode === 'create') {
          await this.$api.post('/admin/banners', payload)
          this.$message.success('创建成功')
        } else {
          await this.$api.put(`/admin/banners/${this.editingId}`, payload)
          this.$message.success('更新成功')
        }
        this.dialogVisible = false
        await this.loadBanners()
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(row, enabled) {
      try {
        await this.$api.put(`/admin/banners/${row.id}/status`, {
          status: enabled ? 'enabled' : 'disabled'
        })
        row.status = enabled ? 'enabled' : 'disabled'
      } catch (e) {
        this.$message.error(formatApiError(e, '状态更新失败'))
        await this.loadBanners()
      }
    },
    async moveBanner(row, direction) {
      try {
        const resp = await this.$api.post(`/admin/banners/${row.id}/move`, { direction })
        if (resp && resp.code === 200) {
          this.banners = Array.isArray(resp.data) ? resp.data : this.banners
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '移动失败'))
      }
    },
    async removeBanner(row) {
      try {
        await this.$confirm('确定删除该轮播？删除后小程序与网页端将同步不再展示。', '提示', {
          type: 'warning'
        })
        await this.$api.delete(`/admin/banners/${row.id}`)
        this.$message.success('已删除')
        await this.loadBanners()
      } catch (e) {
        /* cancel */
      }
    }
  }
}
</script>

<style scoped>
.banner-management {
  padding: 20px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.header-desc {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
}
.form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.banner-table ::v-deep .nowrap-cell .cell {
  white-space: nowrap;
}
.thumb-wrap {
  position: relative;
  width: 120px;
}
.thumb-img {
  width: 120px;
  height: 68px;
  border-radius: 6px;
  display: block;
  flex-shrink: 0;
}
.banner-table ::v-deep .thumb-img img {
  object-fit: cover;
  object-position: center;
}
.thumb-video {
  position: relative;
  width: 120px;
  height: 68px;
}
.thumb-badge {
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 18px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
}
.thumb-error {
  width: 120px;
  height: 68px;
  line-height: 68px;
  text-align: center;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
  border-radius: 6px;
}
.banner-uploader {
  display: inline-block;
}
.banner-preview {
  width: 240px;
  height: 135px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  display: block;
  background: #f5f7fa;
}
.banner-video-preview {
  width: 240px;
  height: 135px;
  border-radius: 8px;
  overflow: hidden;
  background: #0f172a;
}
.banner-preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.banner-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 240px;
  height: 135px;
  line-height: 135px;
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
}
</style>
